import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const IMAGE = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=85";
const IMAGE2 = "https://images.unsplash.com/photo-1613545325268-9265e1609167?auto=format&fit=crop&w=1200&q=85";

const STATS = [
  { n: "14+", l: "Landmark projects" },
  { n: "2.8M", l: "Sq. ft. delivered" },
  { n: "6", l: "Years of quiet work" },
  { n: "100%", l: "On-time handover" },
];

export default function ManifestoV2() {
  return (
    <section
      id="philosophy-v2"
      data-testid="manifesto-v2"
      className="relative bg-charcoal text-charcoal_ink py-24 md:py-36 px-6 md:px-10 overflow-hidden v2-grain"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-16 items-center relative">
        {/* Left: layered images */}
        <div className="col-span-12 md:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] overflow-hidden"
          >
            <img src={IMAGE} alt="KLiving craftsmanship" className="w-full h-full object-cover" />
            {/* Cinematic tint on image */}
            <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/60 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="hidden md:block absolute -bottom-10 -right-10 w-[52%] aspect-[4/3] overflow-hidden border-[10px] border-charcoal shadow-2xl"
          >
            <img src={IMAGE2} alt="Interior detail" className="w-full h-full object-cover" />
          </motion.div>

          {/* Years badge — with glow */}
          <div className="absolute top-6 left-6 bg-charcoal_2 text-charcoal_ink px-4 py-3 flex items-baseline gap-2 border border-glow_lime/40 glow-ring">
            <span className="font-serif-display text-4xl leading-none">06</span>
            <div className="flex flex-col leading-tight">
              <span className="chapter-num glow-lime">YEARS</span>
              <span className="chapter-num text-charcoal_ink/60">OF CRAFT</span>
            </div>
          </div>
        </div>

        {/* Right: about text panel */}
        <div className="col-span-12 md:col-span-6">
          <div className="chapter-num mb-6 flex items-center">
            <span className="inline-block w-8 h-px bg-glow_lime glow-box align-middle mr-3" />
            <span className="glow-lime-soft text-glow_lime">ABOUT KLIVING</span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display leading-[0.95] tracking-tight text-4xl md:text-5xl lg:text-6xl text-charcoal_ink"
          >
            Building landmarks that <span className="italic glow-lime">endure</span>, in Bhubaneswar.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mt-8 text-charcoal_ink/75 text-lg leading-relaxed max-w-xl"
          >
            KLiving is a boutique construction and development company specialising in luxury residential, commercial and modern infrastructure projects. We combine innovative design, high-quality materials and expert craftsmanship to deliver elegant, durable and future-ready spaces.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="mt-4 text-charcoal_ink/65 text-base leading-relaxed max-w-xl"
          >
            Our focus is on excellence, transparency, timely delivery and customer satisfaction — creating properties that reflect sophistication, comfort and long-term value.
          </motion.p>

          {/* Stats grid */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-charcoal_ink/10 pt-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
              >
                <div className="font-serif-display text-4xl md:text-5xl leading-none text-charcoal_ink">{s.n}</div>
                <div className="chapter-num text-charcoal_ink/50 mt-2">{s.l.toUpperCase()}</div>
              </motion.div>
            ))}
          </div>

          <a href="#projects-v2" className="btn-kl-v2 mt-10">
            Read Our Story <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
